import { Component } from '@angular/core';
import { AiAssistService } from '../ai-assist.service';

interface Cond { key: string; label: string; group: string; rate: number; }
interface Layer { key: string; sev: number; }

/**
 * AI Vision Lab — multi-layer eye visualiser.
 *  • Vision Simulator — stack ANY combination of conditions, each with its own
 *    severity; effects compound (blur, glare, tunnel, central scotoma, wavy
 *    distortion, ghosting, colour shift, hemifield loss…).
 *  • Digital Twin — an eye painted live from the active layers.
 *  • Time-Machine — project every layer over years, treated vs untreated.
 * Pure CSS/SVG visuals (demo-proof); DeepSeek writes the prognosis.
 */
@Component({
  selector: 'app-eye-vision-lab',
  templateUrl: './eye-vision-lab.component.html',
  styleUrls: ['./eye-vision-lab.component.scss'],
})
export class EyeVisionLabComponent {
  tab: 'sim' | 'twin' | 'time' = 'sim';

  readonly conditions: Cond[] = [
    // Lens / cornea / refraction
    { key: 'cataract', label: 'Cataract', group: 'Lens & Refraction', rate: 8 },
    { key: 'pco', label: 'PCO (after-cataract)', group: 'Lens & Refraction', rate: 5 },
    { key: 'refractive', label: 'Myopia / blur', group: 'Lens & Refraction', rate: 2 },
    { key: 'astigmatism', label: 'Astigmatism', group: 'Lens & Refraction', rate: 1 },
    { key: 'presbyopia', label: 'Presbyopia', group: 'Lens & Refraction', rate: 3 },
    { key: 'dryeye', label: 'Dry eye', group: 'Lens & Refraction', rate: 2 },
    { key: 'conjunctivitis', label: 'Red eye', group: 'Lens & Refraction', rate: 0 },
    // Glaucoma / neuro
    { key: 'glaucoma', label: 'Glaucoma', group: 'Glaucoma & Neuro', rate: 7 },
    { key: 'rp', label: 'Retinitis pigmentosa', group: 'Glaucoma & Neuro', rate: 6 },
    { key: 'hemianopia', label: 'Hemianopia', group: 'Glaucoma & Neuro', rate: 1 },
    { key: 'nyctalopia', label: 'Night blindness', group: 'Glaucoma & Neuro', rate: 3 },
    { key: 'diplopia', label: 'Double vision', group: 'Glaucoma & Neuro', rate: 1 },
    // Retina / macula
    { key: 'amd', label: 'Macular degeneration', group: 'Retina & Macula', rate: 9 },
    { key: 'dr', label: 'Diabetic retinopathy', group: 'Retina & Macula', rate: 10 },
    { key: 'dme', label: 'Macular edema', group: 'Retina & Macula', rate: 9 },
    { key: 'csr', label: 'Central serous (CSR)', group: 'Retina & Macula', rate: 4 },
    { key: 'vitreous', label: 'Floaters', group: 'Retina & Macula', rate: 2 },
    // Perception
    { key: 'cvd', label: 'Colour blindness', group: 'Perception', rate: 0 },
    { key: 'contrast_loss', label: 'Contrast loss', group: 'Perception', rate: 3 },
    { key: 'halos_night', label: 'Glare / halos', group: 'Perception', rate: 2 },
  ];

  layers: Layer[] = [{ key: 'cataract', sev: 55 }];
  eye: 'RE' | 'LE' | 'BE' = 'RE';
  twinView: 'anterior' | 'fundus' = 'anterior';

  // time-machine
  year = 3;
  treated = false;
  prognosis = '';
  prognosisLoading = false;

  readonly floatSpec = [
    { t: 28, l: 38, s: 26 }, { t: 55, l: 62, s: 34 }, { t: 40, l: 20, s: 18 },
    { t: 68, l: 44, s: 22 }, { t: 22, l: 70, s: 28 }, { t: 60, l: 30, s: 16 },
    { t: 35, l: 55, s: 20 }, { t: 72, l: 66, s: 24 },
  ];

  constructor(private assist: AiAssistService) {}

  // ── layer management ────────────────────────────────────────────────────────
  groups(): string[] { return Array.from(new Set(this.conditions.map((c) => c.group))); }
  condsIn(g: string): Cond[] { return this.conditions.filter((c) => c.group === g); }
  labelOf(k: string): string { return (this.conditions.find((c) => c.key === k) || { label: k }).label; }
  rateOf(k: string): number { return (this.conditions.find((c) => c.key === k) || { rate: 0 }).rate; }
  isOn(k: string): boolean { return this.layers.some((l) => l.key === k); }
  toggle(k: string): void {
    if (this.isOn(k)) { this.layers = this.layers.filter((l) => l.key !== k); }
    else { this.layers.push({ key: k, sev: 50 }); this.twinView = this.viewFor(k); }
    this.prognosis = '';
  }
  remove(k: string): void { this.layers = this.layers.filter((l) => l.key !== k); }
  clearAll(): void { this.layers = []; }
  viewFor(k: string): 'anterior' | 'fundus' {
    return ['glaucoma', 'rp', 'amd', 'dr', 'dme', 'csr'].includes(k) ? 'fundus' : 'anterior';
  }
  setTab(t: 'sim' | 'twin' | 'time'): void {
    this.tab = t;
    if (t === 'twin') { this.twinView = this.pickView(); }
  }

  /** auto-choose the eye view that actually shows the active conditions */
  pickView(): 'anterior' | 'fundus' {
    const fundus = ['glaucoma', 'rp', 'amd', 'dr', 'dme', 'csr'];
    const anterior = ['cataract', 'pco', 'conjunctivitis', 'dryeye'];
    const hasF = this.layers.some((l) => fundus.includes(l.key));
    const hasA = this.layers.some((l) => anterior.includes(l.key));
    if (hasF && !hasA) { return 'fundus'; }
    if (hasA && !hasF) { return 'anterior'; }
    return this.twinView;
  }

  /** active conditions that the twin actually paints (for the caption) */
  get twinPainted(): string[] {
    const painted = ['cataract', 'pco', 'conjunctivitis', 'dryeye', 'glaucoma', 'rp', 'amd', 'dr', 'dme', 'csr'];
    return this.layers.filter((l) => painted.includes(l.key)).map((l) => `${this.labelOf(l.key)} ${l.sev}%`);
  }
  get cornealHaze(): number { return this.cataractOpacity * 0.45; }

  // ── per-layer severity (0..1), tab-aware (projected in time-machine) ─────────
  sevOf(key: string): number {
    const L = this.layers.find((l) => l.key === key);
    if (!L) { return 0; }
    let s = L.sev;
    if (this.tab === 'time') { s = this.project(L.sev, key, this.year, this.treated); }
    return Math.max(0, Math.min(1, s / 100));
  }
  project(base: number, key: string, year: number, treated: boolean): number {
    const rate = this.rateOf(key);
    if (treated) {
      if (key === 'cataract' && year >= 1) { return 3; }   // surgery restores
      return Math.min(100, base + rate * year * 0.12);
    }
    return Math.min(100, base + rate * year);
  }

  // ── per-condition effect contribution ───────────────────────────────────────
  private contrib(key: string, s: number): any {
    switch (key) {
      case 'cataract': return { blur: 4.5 * s, dark: 0.30 * s, con: 1 - 0.28 * s, sep: 0.55 * s, halo: s };
      case 'pco': return { blur: 3 * s, sep: 0.25 * s, halo: 0.4 * s };
      case 'refractive': return { blur: 13 * s };
      case 'astigmatism': return { blur: 4 * s, ghost: 7 * s };
      case 'presbyopia': return { blur: 9 * s };
      case 'dryeye': return { blur: 3 * s, con: 1 - 0.1 * s };
      case 'conjunctivitis': return { blur: 0.8 * s };
      case 'glaucoma': return { tunnel: 88 - 74 * s, blur: 1.5 * s };
      case 'rp': return { tunnel: 90 - 80 * s, dark: 0.45 * s };
      case 'hemianopia': return { hemi: s };
      case 'nyctalopia': return { dark: 0.55 * s };
      case 'diplopia': return { ghost: 9 * s };
      case 'amd': return { scotoma: 6 + 46 * s, distort: s, blur: 1.2 * s };
      case 'dr': return { blur: 2.4 * s, con: 1 - 0.15 * s, floaters: s };
      case 'dme': return { blur: 3 * s, scotoma: 4 + 20 * s, distort: 0.5 * s };
      case 'csr': return { scotoma: 4 + 22 * s, blur: 1.5 * s, dark: 0.1 * s };
      case 'vitreous': return { floaters: s };
      case 'cvd': return { sat: 1 - 0.65 * s, hue: -25 * s };
      case 'contrast_loss': return { con: 1 - 0.5 * s };
      case 'halos_night': return { halo: s, dark: 0.25 * s };
      default: return {};
    }
  }

  // ── aggregate all active layers ─────────────────────────────────────────────
  get fx(): any {
    let blur = 0, bri = 1, con = 1, sep = 0, hue = 0, sat = 1, ghost = 0, distort = 0;
    let tunnel = 100, scotoma = 0, halo = 0, hemi = 0, floaters = 0;
    for (const L of this.layers) {
      const s = this.sevOf(L.key);
      if (s <= 0) { continue; }
      const c = this.contrib(L.key, s);
      blur += c.blur || 0;
      bri *= (1 - (c.dark || 0));
      con *= (c.con != null ? c.con : 1);
      sep = Math.max(sep, c.sep || 0);
      hue += c.hue || 0;
      sat *= (c.sat != null ? c.sat : 1);
      ghost = Math.max(ghost, c.ghost || 0);
      distort = Math.max(distort, c.distort || 0);
      if (c.tunnel != null) { tunnel = Math.min(tunnel, c.tunnel); }
      scotoma = Math.max(scotoma, c.scotoma || 0);
      halo = Math.max(halo, c.halo || 0);
      hemi = Math.max(hemi, c.hemi || 0);
      floaters = Math.max(floaters, c.floaters || 0);
    }
    return { blur, bri, con, sep, hue, sat, ghost, distort, tunnel, scotoma, halo, hemi, floaters };
  }

  get sceneFilter(): string {
    const f = this.fx;
    let s = '';
    if (f.distort > 0) { s += 'url(#vl-distort) '; }
    s += `blur(${f.blur.toFixed(1)}px) brightness(${f.bri.toFixed(2)}) contrast(${f.con.toFixed(2)}) sepia(${f.sep.toFixed(2)}) hue-rotate(${f.hue.toFixed(0)}deg) saturate(${f.sat.toFixed(2)})`;
    if (f.ghost > 0.3) { s += ` drop-shadow(${f.ghost.toFixed(1)}px 0 0 rgba(120,120,150,.55))`; }
    return s;
  }
  get distortScale(): number { return Math.round(this.fx.distort * 26); }

  get showHalo(): boolean { return this.fx.halo > 0.05; }
  get haloStyle(): any {
    const o = Math.min(0.85, this.fx.halo);
    return { opacity: o, background: `radial-gradient(circle at 78% 26%, rgba(255,248,220,${o}) 0%, rgba(255,245,200,${o * 0.5}) 12%, transparent 34%)` };
  }
  get showTunnel(): boolean { return this.fx.tunnel < 98; }
  get tunnelStyle(): any {
    const r = this.fx.tunnel;
    return { background: `radial-gradient(circle at 50% 50%, transparent ${Math.max(8, r - 14)}%, rgba(5,8,18,0.97) ${r}%)` };
  }
  get showScotoma(): boolean { return this.fx.scotoma > 1; }
  get scotomaStyle(): any {
    const sz = this.fx.scotoma;
    return { background: `radial-gradient(circle at 50% 48%, rgba(20,18,28,0.95) ${sz * 0.55}%, rgba(20,18,28,0.6) ${sz}%, transparent ${sz + 12}%)`, filter: `blur(${6 + sz * 0.18}px)` };
  }
  get showHemi(): boolean { return this.fx.hemi > 0.05; }
  get hemiStyle(): any {
    return { opacity: this.fx.hemi, background: 'linear-gradient(90deg, transparent 50%, rgba(4,6,15,0.98) 58%)' };
  }
  get floaters(): any[] {
    if (this.fx.floaters < 0.05) { return []; }
    const n = Math.round(this.fx.floaters * this.floatSpec.length);
    return this.floatSpec.slice(0, n).map((f) => ({
      top: f.t + '%', left: f.l + '%', width: f.s + 'px', height: f.s + 'px',
      opacity: 0.35 + this.fx.floaters * 0.5,
    }));
  }

  // ── digital twin (combines naturally — each layer paints a different part) ───
  get cataractOpacity(): number { return Math.min(0.92, Math.max(this.sevOf('cataract'), this.sevOf('pco') * 0.7)); }
  get scleraRed(): number { return Math.max(this.sevOf('conjunctivitis'), this.sevOf('dryeye') * 0.5, this.sevOf('dr') * 0.15, 0.04); }
  get cupRatio(): number { return 0.3 + this.sevOf('glaucoma') * 0.6; }
  get cupR(): number { return 26 * this.cupRatio; }
  get hemorrhages(): any[] {
    const sev = Math.max(this.sevOf('dr'), this.sevOf('dme'));
    if (sev <= 0) { return []; }
    const n = Math.round(sev * 7);
    const spots = [[120, 90], [150, 140], [95, 150], [175, 105], [110, 175], [165, 175], [85, 115]];
    return spots.slice(0, n).map((p) => ({ cx: p[0], cy: p[1] }));
  }
  get drusen(): any[] {
    const sev = Math.max(this.sevOf('amd'), this.sevOf('csr') * 0.6);
    if (sev <= 0) { return []; }
    const n = Math.round(sev * 9);
    const out = [];
    for (let i = 0; i < n; i++) { const a = i * 0.8, r = 6 + (i % 4) * 5; out.push({ cx: 170 + Math.cos(a) * r, cy: 132 + Math.sin(a) * r }); }
    return out;
  }
  get rpPigment(): any[] {
    const sev = this.sevOf('rp');
    if (sev <= 0) { return []; }
    const n = Math.round(sev * 10);
    const out = [];
    for (let i = 0; i < n; i++) { const a = (i / n) * 6.28, r = 70 + (i % 3) * 14; out.push({ cx: 130 + Math.cos(a) * r, cy: 130 + Math.sin(a) * r }); }
    return out;
  }

  // ── overall readouts ────────────────────────────────────────────────────────
  get impair(): number {
    const f = this.fx;
    return Math.min(1, f.blur / 13 + f.scotoma / 55 + (100 - f.tunnel) / 130 + f.halo * 0.2 + f.hemi * 0.4);
  }
  get acuity(): string {
    const s = this.impair;
    if (s < 0.1) { return '6/6'; }
    if (s < 0.3) { return '6/9'; }
    if (s < 0.5) { return '6/12'; }
    if (s < 0.7) { return '6/24'; }
    if (s < 0.88) { return '6/60'; }
    return 'CF';
  }
  get sevPct(): number { return Math.round(this.impair * 100); }

  // ── time-machine prognosis ──────────────────────────────────────────────────
  getPrognosis(): void {
    this.prognosisLoading = true; this.prognosis = '';
    const list = this.layers.map((l) => `${this.labelOf(l.key)} (${l.sev}%)`).join(' + ') || 'no condition';
    const ctx = `Conditions: ${list}; eye ${this.eye}; ${this.treated ? 'WITH treatment/surgery' : 'left UNTREATED'}.`;
    this.assist.generate('vision_prognosis', ctx, 'en').subscribe({
      next: (r) => { this.prognosisLoading = false; this.prognosis = (r && r.text) || ''; },
      error: () => { this.prognosisLoading = false; this.prognosis = 'Could not reach the AI service.'; },
    });
  }
}
